<template>
  <Dialog
    v-model="useDialogStore().fakturoidEvent.isOpen"
    size="small-hug"
    @open="onOpen"
    @close="onClose"
  >
    <template #header="{ close }">
      <DialogHeader
        :title="useT('fakturoid.event.title')"
        :show-close="true"
        @close="close"
      />
    </template>

    <div
      v-if="isLoading"
      v-loading="true"
      class="h-40 w-full"
    />
    <div
      v-else-if="!isConfigured"
      class="text-sm text-amber-700 bg-amber-50 rounded-lg p-4"
    >
      {{ useT('fakturoid.event.notConfigured') }}
    </div>
    <Form
      v-else
      ref="formRef"
      :model="form"
      @submit="onSubmit"
    >
      <div class="flex items-center justify-between rounded-lg border border-gray-200 p-3 mb-4">
        <div class="mr-4">
          <div class="text-sm font-medium">
            {{ useT('fakturoid.event.isBankTransferAllowed') }}
          </div>
          <div class="text-xs text-gray-500">
            {{ useT('fakturoid.event.isBankTransferAllowed.description') }}
          </div>
        </div>
        <Switch
          :model-value="form.isBankTransferAllowed"
          @update:model-value="(value) => form.isBankTransferAllowed = value"
        />
      </div>

      <InputBlock
        :label="useT('fakturoid.event.invoiceDueDaysOffset')"
        prop="invoiceDueDaysOffset"
        direction="horizontal"
      >
        <NumberInput
          :model-value="form.invoiceDueDaysOffset"
          class="w-full"
          :min="0"
          :max="365"
          @update:model-value="(value) => form.invoiceDueDaysOffset = value"
        />
      </InputBlock>

      <InputBlock
        :label="useT('fakturoid.event.bankAccount')"
        prop="bankTransferAccountId"
        direction="horizontal"
      >
        <TextInput
          v-if="hasBankAccountsFailed"
          :model-value="bankAccountIdText"
          class="w-full"
          name="bankTransferAccountId"
          @update:model-value="(value) => form.bankTransferAccountId = value"
        />
        <Select
          v-else
          :model-value="form.bankTransferAccountId"
          :options="bankAccountOptions"
          @update:model-value="(value) => form.bankTransferAccountId = value"
        />
        <div
          v-if="hasBankAccountsFailed"
          class="text-xs text-amber-700 mt-1 w-full"
        >
          {{ useT('fakturoid.event.bankAccountsError') }}
        </div>
      </InputBlock>
    </Form>

    <template
      v-if="!isLoading && isConfigured"
      #footer
    >
      <ButtonGroup
        type="primary"
        :text-submit="useT('bcd15')"
        :text-cancel="useT('bcd205')"
        align="left"
        :loading="isSubmitting"
        @submit="formRef.onSubmit()"
        @cancel="useDialogStore().close('fakturoidEvent')"
      />
    </template>
  </Dialog>
</template>

<script setup>
// Refs
const form = ref({
  isBankTransferAllowed: false,
  invoiceDueDaysOffset: 14,
  bankTransferAccountId: null,
});

const isLoading = ref(false);
const isSubmitting = ref(false);
const isConfigured = ref(false);
const hasBankAccountsFailed = ref(false);
const bankAccounts = ref([]);
const formRef = ref(null);

// Computed
// '' is the "no account selected" sentinel — the Select component doesn't
// accept null as a model value; it's coerced back to null on submit.
const bankAccountOptions = computed(() => [
  { id: '', text: useT('fakturoid.event.bankAccountPlaceholder') },
  ...bankAccounts.value.map((account) => ({
    id: account.id,
    text: `${account.name} (${account.number}) — ${String(account.currency || '').toUpperCase()}${account.isDefault ? ` · ${useT('fakturoid.event.default')}` : ''}`,
  })),
]);

// The manual fallback TextInput is String-typed; '' / null both render empty.
const bankAccountIdText = computed(() => (form.value.bankTransferAccountId === '' || form.value.bankTransferAccountId == null
  ? ''
  : String(form.value.bankTransferAccountId)));

// Methods
const onOpen = async() => {
  isLoading.value = true;

  const workspaceDomain = useDialogStore().fakturoidEvent.workspaceDomain;

  // Live, not from the grid's row, which comes from the catalogue.
  const [ticketing, settings] = await Promise.all([
    useFakturoidStore().fetchEventTicketing({
      eventId: useDialogStore().fakturoidEvent.event?._id,
      workspaceDomain,
    }),
    useFakturoidStore().fetchWorkspaceSettings(workspaceDomain),
  ]);

  if (!ticketing || !settings) {
    useDialogStore().close('fakturoidEvent');
    return;
  }

  form.value = {
    isBankTransferAllowed: Boolean(ticketing.isBankTransferAllowed),
    invoiceDueDaysOffset: ticketing.invoiceDueDaysOffset ?? 14,
    bankTransferAccountId: ticketing.bankTransferAccountId ?? '',
  };

  isConfigured.value = Boolean(settings.isEnabled);

  if (isConfigured.value) {
    const response = await useFakturoidStore().fetchBankAccounts(workspaceDomain);

    if (response) {
      bankAccounts.value = response.results;
      hasBankAccountsFailed.value = false;
    } else {
      hasBankAccountsFailed.value = true;
    }
  }

  isLoading.value = false;
};

const onClose = () => {
  // form is rebuilt by onOpen on every reopen; only the surrounding state needs resetting.
  bankAccounts.value = [];
  hasBankAccountsFailed.value = false;
  isConfigured.value = false;
};

const onSubmit = async() => {
  isSubmitting.value = true;

  const response = await useFakturoidStore().saveEventTicketing({
    eventId: useDialogStore().fakturoidEvent.event?._id,
    body: {
      workspaceDomain: useDialogStore().fakturoidEvent.workspaceDomain,
      isBankTransferAllowed: form.value.isBankTransferAllowed,
      invoiceDueDaysOffset: form.value.invoiceDueDaysOffset ?? 14,
      // '' (no selection) → null; the manual text-input fallback yields a
      // string, so coerce to the integer account ID the API expects.
      bankTransferAccountId: form.value.bankTransferAccountId === '' || form.value.bankTransferAccountId == null
        ? null
        : Number(form.value.bankTransferAccountId),
    },
  });

  isSubmitting.value = false;

  if (response) {
    useToast(useT('fakturoid.event.saved'));
    useDialogStore().fakturoidEvent.onSubmit(response);
    useDialogStore().close('fakturoidEvent');
  }
};
</script>
